import { createSlice, CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';

import { Toast } from '@10d/tend-ui/primitives/Toast';

import { get, post, patch } from 'api/requests';

import { AppThunk } from 'store/types';
import { IParamsReq, ISubmitValues } from 'types';
import {
  PlanEdit,
  PlansEngineering,
  PlanNetworksDataSource,
  PlansEngineeringStore,
  EngineeringPlansNetworks,
} from 'features/Plan/PlanEngineering/types';

const initialState: PlansEngineeringStore = {
  data: [],
  loading: false,
  current: {} as PlanEdit,
  networks: {} as EngineeringPlansNetworks,
  freeNetworks: [],
};

const reset: CaseReducer<PlansEngineeringStore> = () => initialState;

const loading: CaseReducer<PlansEngineeringStore, PayloadAction<boolean>> = (state, action) => {
  state.loading = action.payload;
};

const current: CaseReducer<PlansEngineeringStore, PayloadAction<PlanEdit>> = (state, action) => {
  state.current = action.payload;
};

const data: CaseReducer<PlansEngineeringStore, PayloadAction<PlansEngineering[]>> = (
  state,
  action,
) => {
  state.data = action.payload;
};

const freeNetworks: CaseReducer<PlansEngineeringStore, PayloadAction<PlanNetworksDataSource[]>> = (
  state,
  action,
) => {
  state.freeNetworks = action.payload;
};

const networks: CaseReducer<PlansEngineeringStore, PayloadAction<EngineeringPlansNetworks>> = (
  state,
  action,
) => {
  state.networks = action.payload;
};

export const planEngineeringSlice = createSlice({
  name: 'planEngineering',
  initialState,
  reducers: {
    reset,
    loading,
    data,
    current,
    freeNetworks,
    networks,
  },
});

const {
  data: setData,
  reset: setReset,
  loading: setLoading,
  current: setCurrentPlans,
  freeNetworks: setFreeNetworks,
  networks: setNetworks,
} = planEngineeringSlice.actions;

export const setPlans =
  (plans: PlansEngineering[]): AppThunk =>
  dispatch => {
    dispatch(setData(plans));
  };

export const getEngineeringPlans =
  (params: IParamsReq): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<PlansEngineering[]>('/api/settlement/en-plan/grouped-plans/', params)
      .then(({ data }) => {
        dispatch(setPlans(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getFreeNetworks =
  (params?: IParamsReq): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<PlanNetworksDataSource[]>('/api/settlement/en-plan/free-networks/', params)
      .then(({ data }) => {
        dispatch(setFreeNetworks(isEmpty(data) ? [] : data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getCurrentPlan =
  (structure: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));
    /* eslint-disable @typescript-eslint/no-explicit-any */ /* TODO type */
    get<any>(`/api/settlement/en-plan/structures-plans/${structure}/`)
      .then(({ data }) => {
        dispatch(setNetworks(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const clearFreeSections = (): AppThunk => dispatch => {
  dispatch(setLoading(true));
  dispatch(setFreeNetworks([]));
  dispatch(setLoading(false));
};

export const createNewPlan =
  (values: ISubmitValues, callback: () => void): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    post('/api/settlement/en-plan/', values)
      .then(() => Toast.success({ message: 'План успешно создан' }))
      .then(() => callback())
      .finally(() => dispatch(setLoading(false)));
  };

export const updatePlan =
  (values: ISubmitValues, callback: () => void): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    patch('/api/settlement/en-plan/update-list/', values)
      .then(() => Toast.success({ message: 'План успешно сохранен' }))
      .then(() => callback())
      .finally(() => dispatch(setLoading(false)));
  };

export { setReset, setCurrentPlans, setFreeNetworks, setNetworks };
